import { ref, computed, watch } from 'vue';
import { defineStore } from 'pinia';
import { getSessionUserId, getStoredCompanyKey } from '@/services/api/erpat-client';
import { tokenIdentity } from '@/services/offline/offline-scope';
import type { ReminderLead } from '@/types/event';
import { DEFAULT_LEAD, parseLead } from '@/services/event-reminders-plan';

export type ThemeMode = 'system' | 'light' | 'dark';

const STORAGE_KEY = 'syntry_theme';
const LANGUAGE_STORAGE_KEY = 'syntry_language';

/**
 * Event reminder lead. Scoped by tenant AND user — unlike theme and language,
 * this is a per-account server-backed preference, and one device is routinely
 * shared between accounts.
 */
const EVENT_REMINDER_LEAD_PREFIX = 'syntry_event_reminder_lead:';

/** The server's default when a user has never chosen (see the API contract). */
const DEFAULT_EVENT_REMINDER_LEAD: ReminderLead = DEFAULT_LEAD;

/**
 * A lead is a GRAMMAR, not a list — `off`, or an amount plus `m`/`h`/`d` inside the
 * bounds. Testing membership of a fixed array is exactly what capped this preference at
 * three values; parseLead() is the shared gate, mirrored from the server.
 */
function isReminderLead(value: unknown): value is ReminderLead {
  return parseLead(value) !== null;
}

const LANGUAGE_CODES = ['en', 'fil', 'ceb', 'ilo', 'hil'] as const;

/** Currently only 'en' is selectable; other codes are reserved for future locales. */
export type LanguageCode = (typeof LANGUAGE_CODES)[number];

function getStoredLanguage(): LanguageCode {
  const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
  return LANGUAGE_CODES.includes(stored as LanguageCode) ? (stored as LanguageCode) : 'en';
}

const CUTOFF_ORDER_STORAGE_KEY = 'syntry_timesheet_cutoff_order';
const CUTOFF_ORDERS = ['asc', 'desc'] as const;

/**
 * Vertical order of the day rows in the Timesheet's payroll cut-off list:
 * `asc` = oldest date at the top (the original behaviour), `desc` = newest
 * at the top. Device-wide and local-only, like theme and language — the
 * server's preferences endpoint has no field for it — and, like them, not
 * reset on sign-out: it describes how this device shows a list, not an account.
 */
export type CutoffOrder = (typeof CUTOFF_ORDERS)[number];

function getStoredCutoffOrder(): CutoffOrder {
  const stored = localStorage.getItem(CUTOFF_ORDER_STORAGE_KEY);
  return CUTOFF_ORDERS.includes(stored as CutoffOrder) ? (stored as CutoffOrder) : 'asc';
}

/**
 * The department the leave "For Review" list is narrowed to. Persists like the
 * cut-off order — on this device, local-only, NOT reset on sign-out — but it is
 * scoped by tenant AND user like the reminder lead: an approver's department is
 * about their account, and one device is routinely shared between accounts.
 */
const LEAVE_REVIEW_DEPARTMENT_PREFIX = 'syntry_leave_review_department:';

/** The saved filter: the department's id (sent as `department_id`) and its name, for the label. */
export interface LeaveReviewDepartment {
  id: number;
  title: string;
}

function parseLeaveReviewDepartment(raw: string | null): LeaveReviewDepartment | null {
  if (!raw) return null;
  try {
    const value: unknown = JSON.parse(raw);
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    const { id, title } = value as Record<string, unknown>;
    if (typeof id !== 'number' || !Number.isInteger(id) || id < 1) return null;
    if (typeof title !== 'string' || title.trim() === '') return null;
    return { id, title: title.trim() };
  } catch {
    return null;
  }
}

export const useSettingsStore = defineStore('settings', () => {
  const themeMode = ref<ThemeMode>(
    (localStorage.getItem(STORAGE_KEY) as ThemeMode | null) ?? 'system',
  );

  const language = ref<LanguageCode>(getStoredLanguage());

  function setLanguage(code: LanguageCode): void {
    language.value = code;
    localStorage.setItem(LANGUAGE_STORAGE_KEY, code);
  }

  /** Order of the day rows under the Timesheet cut-off's "View Cutoff". */
  const timesheetCutoffOrder = ref<CutoffOrder>(getStoredCutoffOrder());

  function setTimesheetCutoffOrder(order: CutoffOrder): void {
    timesheetCutoffOrder.value = order;
    try {
      localStorage.setItem(CUTOFF_ORDER_STORAGE_KEY, order);
    } catch {
      /* private mode / quota — the choice just doesn't survive a reload */
    }
  }

  /**
   * How far ahead of an event the user is reminded.
   *
   * A per-user GLOBAL setting held on the server, mirrored here so the in-app
   * banner and the Android notification can be planned without waiting on a
   * request. The server copy is authoritative (it schedules the ERPat bell
   * notification); `adoptServerEventReminderLead` is how it wins.
   */
  const eventReminderLead = ref<ReminderLead>(DEFAULT_EVENT_REMINDER_LEAD);

  function eventReminderLeadKey(): string {
    const tenant = (getStoredCompanyKey() ?? '').trim().toLowerCase();
    return `${EVENT_REMINDER_LEAD_PREFIX}${tenant}:${getSessionUserId()}`;
  }

  /**
   * Load the stored lead for the signed-in account.
   *
   * A function rather than initial state because the store is created before
   * anyone is signed in: the tenant and user id that key the value simply do
   * not exist at that point, so an eager read would look under `::` and then
   * never correct itself.
   */
  function hydrateEventReminderLead(): void {
    try {
      // parseLead() canonicalises ('007h' -> '7h'), so a hand-edited or legacy value
      // resolves to the same token the server would have stored.
      const stored = parseLead(localStorage.getItem(eventReminderLeadKey()));
      eventReminderLead.value = stored ? stored.lead : DEFAULT_EVENT_REMINDER_LEAD;
    } catch {
      eventReminderLead.value = DEFAULT_EVENT_REMINDER_LEAD;
    }
  }

  /**
   * The user picked a lead. Persists locally; the caller is responsible for
   * pushing it to the server and for calling `rescheduleEventReminders()` —
   * kept out of here so the store never imports the reminder engine (which
   * imports this store).
   */
  function setEventReminderLead(lead: ReminderLead): void {
    eventReminderLead.value = lead;
    try {
      localStorage.setItem(eventReminderLeadKey(), lead);
    } catch {
      /* private mode / quota — the choice just doesn't survive a reload */
    }
  }

  /** Take the server's value on session start, without echoing it back. */
  function adoptServerEventReminderLead(lead: ReminderLead): void {
    if (!isReminderLead(lead) || lead === eventReminderLead.value) return;
    setEventReminderLead(lead);
  }

  /** The department the leave "For Review" list is narrowed to; `null` = every department. */
  const leaveReviewDepartment = ref<LeaveReviewDepartment | null>(null);

  /**
   * `null` while nobody is signed in: there is no account to key the choice by.
   *
   * The account comes from `tokenIdentity()`, not `getSessionUserId()`: ERPat mints
   * the token's `sub` as a NUMBER, which `getSessionUserId()` reads as '' (left that
   * way on purpose — offline attendance D16 — so other features' keys don't move),
   * and keying by it would put every account's choice under one empty user.
   */
  function leaveReviewDepartmentKey(): string | null {
    const identity = tokenIdentity();
    if (!identity) return null;
    return `${LEAVE_REVIEW_DEPARTMENT_PREFIX}${identity.companyKey}:${identity.userId}`;
  }

  /**
   * Load the signed-in account's saved department. Called by the review list each
   * time it mounts — not at store creation, which happens before anyone is signed
   * in — so a different account on the same device never inherits the value.
   */
  function hydrateLeaveReviewDepartment(): void {
    const key = leaveReviewDepartmentKey();
    let stored: string | null = null;
    try {
      stored = key ? localStorage.getItem(key) : null;
    } catch {
      stored = null;
    }
    leaveReviewDepartment.value = parseLeaveReviewDepartment(stored);
  }

  /** Narrow the review list to a department, or pass `null` for every department. */
  function setLeaveReviewDepartment(department: LeaveReviewDepartment | null): void {
    leaveReviewDepartment.value = department ? { id: department.id, title: department.title } : null;
    const key = leaveReviewDepartmentKey();
    if (!key) return;
    try {
      if (department) localStorage.setItem(key, JSON.stringify(leaveReviewDepartment.value));
      else localStorage.removeItem(key);
    } catch {
      /* private mode / quota — the choice just doesn't survive a reload */
    }
  }

  const systemIsDark = ref(
    window.matchMedia('(prefers-color-scheme: dark)').matches,
  );

  const isDark = computed<boolean>(() => {
    if (themeMode.value === 'dark') return true;
    if (themeMode.value === 'light') return false;
    return systemIsDark.value;
  });

  function applyTheme(): void {
    document.body.classList.toggle('dark', isDark.value);
  }

  function setThemeMode(mode: ThemeMode): void {
    themeMode.value = mode;
    localStorage.setItem(STORAGE_KEY, mode);
    applyTheme();
  }

  function initTheme(): void {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    systemIsDark.value = mq.matches;

    mq.addEventListener('change', (e: MediaQueryListEvent) => {
      systemIsDark.value = e.matches;
      if (themeMode.value === 'system') applyTheme();
    });

    applyTheme();
  }

  // Keep DOM in sync whenever isDark changes (e.g. system pref flips)
  watch(isDark, applyTheme);

  return {
    themeMode,
    isDark,
    systemIsDark,
    setThemeMode,
    initTheme,
    language,
    setLanguage,
    timesheetCutoffOrder,
    setTimesheetCutoffOrder,
    eventReminderLead,
    hydrateEventReminderLead,
    setEventReminderLead,
    adoptServerEventReminderLead,
    leaveReviewDepartment,
    hydrateLeaveReviewDepartment,
    setLeaveReviewDepartment,
  };
});
